import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../firebase/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    console.log("🔧 AuthContext: Setting up onAuthStateChanged listener");
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("🔔 AuthContext: Auth state changed");
      console.log("🔍 AuthContext: Previous loading state:", loading);
      
      setUser(currentUser);
      console.log("✅ AuthContext: User state updated in context");

      if (currentUser) {
        try {
          console.log("🔄 AuthContext: Refreshing Firebase ID token...");
          const token = await currentUser.getIdToken(true);
          console.log("✅ AuthContext: Firebase ID Token refreshed");
          localStorage.setItem("authToken", token);
          console.log("✅ AuthContext: Token saved to localStorage");
        } catch (error) {
          console.error("❌ AuthContext: Token refresh failed");
          setAuthError(error.message);
        }
      } else {
        console.log("🗑️ AuthContext: Removing authToken from localStorage");
        localStorage.removeItem("authToken");
      }
      setLoading(false);
      console.log("✅ AuthContext: Loading set to false");
    });
    return () => {
      console.log("🧹 AuthContext: Cleaning up onAuthStateChanged listener");
      unsubscribe();
    };
  }, []);

  const signUp = async (email, password) => {
    try {
      setAuthError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken(true);
      localStorage.setItem("authToken", token);
      return userCredential;
    } catch (error) {
      console.error("❌ Signup error:", error.message);
      setAuthError(error.message);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    try {
      setAuthError(null);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await userCredential.user.getIdToken(true);
      localStorage.setItem("authToken", token);
      return userCredential;
    } catch (error) {
      console.error("❌ Login error:", error.message);
      setAuthError(error.message);
      throw error;
    }
  };

  // ✅ FIXED: Standard Google Sign-In
  const signInWithGoogle = async () => {
    try {
      setAuthError(null);
      const provider = new GoogleAuthProvider();
      
      // Add scopes if needed
      provider.addScope('email');
      provider.addScope('profile');
      
      console.log("🔄 Starting Google sign-in popup...");
      const result = await signInWithPopup(auth, provider);
      
      const token = await result.user.getIdToken(true);
      localStorage.setItem("authToken", token);
      
      console.log("✅ Google sign-in successful");
      return result;
    } catch (error) {
      console.error("❌ Google sign-in error");
      setAuthError(error.message);
      throw error;
    }
  };

  const logOut = async () => {
    try {
      setAuthError(null);
      await signOut(auth);
      localStorage.removeItem("authToken");
      console.log("✅ User signed out successfully");
    } catch (error) {
      console.error("❌ Logout error:", error.message);
      setAuthError(error.message);
      throw error;
    }
  };

  const clearError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        signUp,
        signIn,
        logOut,
        signInWithGoogle,
        authError,
        clearError,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
